import { copyToClipboard } from '@xver/utils/clipboard';
import { AreaChart } from '@mantine/charts';
import { Breadcrumbs, Center, Loader, Menu, SegmentedControl, Tabs } from '@mantine/core';
import { useParams } from 'react-router';

import { useDisclosure } from '@mantine/hooks';
import {
    IconAffiliate,
    IconCheck,
    IconCircleFilled,
    IconClock,
    IconCopy,
    IconDatabase,
    IconDots,
    IconFolderShare,
    IconLayoutDashboard,
    IconPlayerPause,
    IconPlayerPlay,
    IconPlugConnected,
    IconPlus,
    IconReload,
    IconSettings,
    IconShieldCheck,
    IconSquare,
    IconTerminal,
    IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessBadge, Avatar, Button } from '../../../../components/Atoms';
import DialogBox from '../../../../components/Atoms/DialogBox/DialogBox';
import { ConnectInstanceModal, toConnectTarget } from '../../../../components/Organisms';
import { useDeleteInstanceTagMutate } from '../../../../hooks/CloudComputeHooks/useDeleteInstanceTagMutate';
import {
    openInstanceConsole,
    useInstanceConsoleMutate,
} from '../../../../hooks/CloudComputeHooks/useInstanceConsoleMutate';
import { useInvokeActionMutate } from '../../../../hooks/CloudComputeHooks/useInvokeActionMutate';
import { useToggleBackupMutate } from '../../../../hooks/CloudComputeHooks/useToggleBackupMutate';
import {
    useActiveProjectScope,
    useAlignActiveProject,
    useProductAccess,
} from '../../../../hooks/useActiveProject';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import { moveTriggerProps, useMoveResource } from '../../../../hooks/useMoveResource';
import usePageHeading from '../../../../hooks/usePageHeading';
import { getOSFullName } from '../../../../utils/os';
import AttachedVolumesPanel from '../BlockStoragePage/components/AttachedVolumesPanel';
import ActivityCard from './components/ActivityCard';
import ActivityTabPanel from './components/ActivityTabPanel';
import BackupTabPanel from './components/BackupTabPanel';
import CostCard from './components/CostCard';
import ManageTagsModal from './components/ManageTagsModal';
import NetworkingTabPanel from './components/NetworkingTabPanel';
import PrivateNetworkAlert from './components/PrivateNetworkAlert';
import SettingsTabPanel from './components/SettingsTabPanel';
import { useInstanceQuery } from './hooks/useInstanceQuery';
import { usePerformanceMetrics } from './hooks/usePerformanceMetrics';

// Plan label per cloud package category; legacy instances (no package field)
// fall back to shared — the lineup that predates tiering.
const PLAN_VALUE_KEY_BY_CATEGORY: Record<string, string> = {
    shared: 'systemInformation.planValueShared',
    general: 'systemInformation.planValueGeneral',
    dedicated: 'systemInformation.planValueDedicated',
};

export default function CloudComputeInstancePage() {
    const { t } = useTranslation('cloudComputeInstance');
    const { instanceId } = useParams<'instanceId'>();

    const {
        data: instanceData,
        isPending: isInstancePending,
        refetch: refetchInstance,
    } = useInstanceQuery(Number(instanceId));
    useAlignActiveProject(instanceData?.projectId);
    const { activeProjectId } = useActiveProjectScope();
    const blockStorageAccess = useProductAccess('block_storage');
    const showBlockStorage = blockStorageAccess === 'view' || blockStorageAccess === 'manage';
    const { canMove, requestMove, moveModal } = useMoveResource('instance', {
        triggerStaysAfterMove: true,
    });
    const headingRef = usePageHeading(instanceData?.hostname, t('breadcrumbs.cloudCompute'));

    const metrics = usePerformanceMetrics(instanceData?.state.cpu, instanceData?.state.memory);

    const [chartFilter, setChartFilter] = useState<'both' | 'cpu' | 'memory'>('both');

    const currentCpu =
        instanceData?.state.cpu !== undefined ? (instanceData.state.cpu * 100).toFixed(2) : null;
    const currentMemory =
        instanceData?.state.memory !== undefined ? instanceData.state.memory.toFixed(2) : null;
    const currentLoadAvg =
        instanceData?.state.loadavg !== undefined ? instanceData.state.loadavg.toFixed(2) : null;

    const toGB = (bytes: number) => (bytes / 1024 / 1024 / 1024).toFixed(1);
    const memoryUsedGB =
        instanceData?.state.memoryBytes !== undefined ? toGB(instanceData.state.memoryBytes) : null;
    const memoryMaxGB =
        instanceData?.state.maxMemoryBytes !== undefined
            ? toGB(instanceData.state.maxMemoryBytes)
            : null;
    const memoryFreeGB =
        instanceData?.state.memoryBytes !== undefined &&
        instanceData?.state.maxMemoryBytes !== undefined
            ? toGB(instanceData.state.maxMemoryBytes - instanceData.state.memoryBytes)
            : null;

    const formatUptime = (seconds?: number | null) => {
        if (seconds === undefined || seconds === null) return '--';
        const s = Math.max(0, Math.floor(seconds));
        const years = Math.floor(s / 31536000);
        const months = Math.floor((s % 31536000) / 2592000);
        const days = Math.floor((s % 2592000) / 86400);
        const hours = Math.floor((s % 86400) / 3600);
        const minutes = Math.floor((s % 3600) / 60);

        if (years > 0)
            return years === 1
                ? t('systemInformation.uptimeYear')
                : t('systemInformation.uptimeYears', { count: years });
        if (months > 0)
            return months === 1
                ? t('systemInformation.uptimeMonth')
                : t('systemInformation.uptimeMonths', { count: months });

        if (days > 0) {
            const parts = [
                days === 1
                    ? t('systemInformation.uptimeDay')
                    : t('systemInformation.uptimeDays', { count: days }),
            ];
            if (hours > 0) parts.push(`${hours}h`);
            if (minutes > 0) parts.push(`${minutes}m`);
            return parts.join(', ');
        }

        if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        if (minutes > 0) return `${minutes}m`;
        return t('systemInformation.uptimeSeconds', { count: s });
    };

    const chartSeries = useMemo(() => {
        const all = [
            { name: 'cpu', label: 'CPU', color: 'green.6' },
            { name: 'memory', label: 'Memory', color: 'blue.6' },
        ];
        if (chartFilter === 'cpu') return [all[0]];
        if (chartFilter === 'memory') return [all[1]];
        return all;
    }, [chartFilter]);

    const stats = useMemo(() => {
        if (!metrics || metrics.length === 0) return null;

        const cpuVals = metrics.map((m) => m.cpu);
        const memVals = metrics.map((m) => m.memory);

        const toPercents = (arr: number[]) => {
            if (arr.length === 0) return [] as number[];
            const max = Math.max(...arr);
            if (max <= 1) return arr.map((v) => v * 100);
            return arr.slice();
        };

        const cpuP = toPercents(cpuVals);
        const memP = toPercents(memVals);

        const summarize = (arr: number[]) => {
            if (arr.length === 0) return { min: 0, avg: 0, max: 0 };
            const min = Math.min(...arr);
            const max = Math.max(...arr);
            const avg = arr.reduce((s, v) => s + v, 0) / arr.length;
            return { min, avg, max };
        };

        return {
            cpu: summarize(cpuP),
            memory: summarize(memP),
        };
    }, [metrics]);

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumbs.product'), href: '#' },
        { title: t('breadcrumbs.cloudCompute'), href: '/cloud-compute' },
        { title: instanceData?.hostname ?? '...' },
    ]);

    const [powerState, setPowerState] = useState<string>(instanceData?.state?.power ?? '');
    const [segValue, setSegValue] = useState<string>(instanceData?.state?.power ?? '');
    const [copiedHostname, setCopiedHostname] = useState(false);
    const [copiedIpAddress, setCopiedIpAddress] = useState(false);
    const [copiedPrivateIpAddress, setCopiedPrivateIpAddress] = useState(false);

    useEffect(() => {
        setPowerState(instanceData?.state?.power ?? '');
        if (instanceData?.state?.power === 'starting') {
            setSegValue('running');
        } else if (instanceData?.state?.power === 'stopping') {
            setSegValue('stopped');
        } else if (instanceData?.state?.power === 'rebooting') {
            setSegValue('reboot');
        } else {
            setSegValue(instanceData?.state?.power ?? '');
        }
    }, [instanceData?.state?.power]);

    useEffect(() => {
        if (!copiedIpAddress) return;

        const timeoutId = window.setTimeout(() => {
            setCopiedIpAddress(false);
        }, 1500);

        return () => window.clearTimeout(timeoutId);
    }, [copiedIpAddress]);

    useEffect(() => {
        if (!copiedPrivateIpAddress) return;

        const timeoutId = window.setTimeout(() => {
            setCopiedPrivateIpAddress(false);
        }, 1500);

        return () => window.clearTimeout(timeoutId);
    }, [copiedPrivateIpAddress]);

    useEffect(() => {
        if (!copiedHostname) return;

        const timeoutId = window.setTimeout(() => {
            setCopiedHostname(false);
        }, 1500);

        return () => window.clearTimeout(timeoutId);
    }, [copiedHostname]);

    const [connectHintOpened, { open: openConnectHint, close: closeConnectHint }] = useDisclosure();
    const [manageTagsOpened, { open: openManageTags, close: closeManageTags }] = useDisclosure();
    const [activeTab, setActiveTab] = useState<string | null>('overview');

    const invokeActionMutation = useInvokeActionMutate();
    const instanceConsoleMutation = useInstanceConsoleMutate();
    const toggleBackupMutation = useToggleBackupMutate();
    const deleteInstanceTagMutation = useDeleteInstanceTagMutate();

    const handlePowerStateChange = (value: string) => {
        if (!instanceData) return;

        const previousPowerState = powerState;
        const previousSegValue = segValue;
        const nextAction =
            value === 'running' ? 'start' : value === 'stopped' ? 'shutdown' : 'reboot';

        switch (value) {
            case 'running':
                setSegValue('running');
                setPowerState('starting');
                break;
            case 'stopped':
                setSegValue('stopped');
                setPowerState('stopping');
                break;
            case 'reboot':
                setSegValue('reboot');
                setPowerState('rebooting');
                break;
            default:
                return;
        }

        invokeActionMutation.mutate(
            { id: instanceData.id, action: nextAction },
            {
                onError: () => {
                    setPowerState(previousPowerState);
                    setSegValue(previousSegValue);
                },
            }
        );
    };

    if (!instanceData || isInstancePending) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center">
                <Loader size="lg" type="bars" />
            </div>
        );
    }

    const canManage = instanceData.access !== 'view';
    const visibleTab =
        (!canManage && activeTab === 'settings') ||
        (!showBlockStorage && activeTab === 'block-storage')
            ? 'overview'
            : activeTab;

    return (
        <>
            <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
                <div className="w-full flex flex-col justify-start items-start">
                    <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                        {breadcrumbItems}
                    </Breadcrumbs>
                    <div className="w-full h-auto flex flex-col xl:flex-row justify-between items-start xl:items-end gap-2 xl:gap-8">
                        <div className="flex flex-row justify-start items-center gap-3">
                            <Avatar value={instanceData.hostname} size={'64'} />
                            <div className="flex flex-col justify-start items-start">
                                <div className="flex flex-row flex-wrap justify-start items-center gap-3">
                                    <h1
                                        ref={headingRef}
                                        tabIndex={-1}
                                        className="text-2xl font-bold"
                                    >
                                        {instanceData.hostname}
                                    </h1>
                                    {!canManage && <AccessBadge level="view" />}
                                    {(powerState === 'starting' ||
                                        powerState === 'stopping' ||
                                        powerState === 'rebooting' ||
                                        powerState === 'resetting' ||
                                        powerState === 'backing-up' ||
                                        powerState === 'resizing') && (
                                        <Loader
                                            size={20}
                                            type="oval"
                                            color={
                                                powerState === 'starting'
                                                    ? '#7ce0a4'
                                                    : powerState === 'stopping'
                                                      ? '#ff6b6b'
                                                      : powerState === 'rebooting'
                                                        ? '#ffa726'
                                                        : powerState === 'resetting'
                                                          ? '#ffa726'
                                                          : powerState === 'backing-up'
                                                            ? '#a78bfa'
                                                            : powerState === 'resizing'
                                                              ? '#60a5fa'
                                                              : 'var(--surface-2)'
                                            }
                                        />
                                    )}
                                </div>
                                <div className="text-sm flex flex-row flex-wrap justify-start items-center gap-x-1 text-fg/68">
                                    <span>{getOSFullName(instanceData.image.slug)}</span>
                                    <span className="text-3xl">·</span>
                                    <span>
                                        {instanceData.spec.cpu} {t('header.vcpu')}
                                    </span>
                                    <span className="text-3xl">·</span>
                                    <span>
                                        {instanceData.spec.memory / 1024} {t('header.ramGB')}
                                    </span>
                                    <span className="text-3xl">·</span>
                                    <span>
                                        {instanceData.spec.disk} {t('header.diskGBNvme')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="w-full xl:w-auto flex flex-wrap xl:flex-nowrap justify-start xl:justify-end items-center gap-2">
                            {canManage && (
                                <SegmentedControl
                                    aria-label={t('header.powerControl')}
                                    size="sm"
                                    radius={'md'}
                                    value={segValue}
                                    styles={{
                                        root: {
                                            width: '100%',
                                            backgroundColor: 'var(--surface-2)',
                                            border: '0.5px solid var(--line)',
                                        },
                                    }}
                                    onChange={(value) => handlePowerStateChange(value)}
                                    disabled={
                                        segValue === 'backing-up' ||
                                        segValue === 'resizing' ||
                                        segValue === ''
                                    }
                                    withItemsBorders={false}
                                    data={[
                                        {
                                            value: 'running',
                                            disabled:
                                                segValue === 'starting' ||
                                                segValue === 'stopping' ||
                                                segValue === 'rebooting',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    {powerState === 'starting' ? (
                                                        <Loader size={13} color="#7ce0a4" />
                                                    ) : (
                                                        <IconPlayerPlay
                                                            size={13}
                                                            stroke={2}
                                                            color={
                                                                segValue === 'running'
                                                                    ? '#7ce0a4'
                                                                    : 'oklab(1 0 0 / 0.58)'
                                                            }
                                                        />
                                                    )}
                                                    <span
                                                        className={
                                                            segValue === 'running' ||
                                                            powerState === 'starting'
                                                                ? 'text-[#7ce0a4] light:text-green-800'
                                                                : 'text-fg/68'
                                                        }
                                                        style={{ fontWeight: 400 }}
                                                    >
                                                        {powerState === 'starting'
                                                            ? t('header.powerStarting')
                                                            : segValue === 'running'
                                                              ? t('header.powerRunning')
                                                              : t('header.powerStart')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'stopped',
                                            disabled:
                                                segValue === 'starting' ||
                                                segValue === 'stopping' ||
                                                segValue === 'rebooting',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    {powerState === 'stopping' ? (
                                                        <Loader size={13} color="#ff8b8f" />
                                                    ) : (
                                                        <IconSquare
                                                            size={13}
                                                            stroke={2}
                                                            color={
                                                                segValue === 'stopped'
                                                                    ? '#ff8b8f'
                                                                    : 'oklab(1 0 0 / 0.58)'
                                                            }
                                                        />
                                                    )}
                                                    <span
                                                        style={{ fontWeight: 400 }}
                                                        className={
                                                            segValue === 'stopped' ||
                                                            powerState === 'stopping'
                                                                ? 'text-[#ff8b8f] light:text-red-700'
                                                                : 'text-fg/68'
                                                        }
                                                    >
                                                        {powerState === 'stopping'
                                                            ? t('header.powerStopping')
                                                            : segValue === 'stopped'
                                                              ? t('header.powerStopped')
                                                              : t('header.powerStop')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'reboot',
                                            disabled:
                                                segValue !== 'rebooting' && segValue !== 'running',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    {powerState === 'rebooting' ? (
                                                        <Loader size={13} color="#f5b945" />
                                                    ) : (
                                                        <IconReload
                                                            size={13}
                                                            stroke={2}
                                                            color={
                                                                powerState === 'rebooting'
                                                                    ? '#f5b945'
                                                                    : 'oklab(1 0 0 / 0.58)'
                                                            }
                                                        />
                                                    )}
                                                    <span
                                                        style={{ fontWeight: 400 }}
                                                        className={
                                                            segValue === 'reboot' ||
                                                            powerState === 'rebooting'
                                                                ? 'text-[#f5b945] light:text-yellow-800'
                                                                : 'text-fg/68'
                                                        }
                                                    >
                                                        {powerState === 'rebooting'
                                                            ? t('header.powerRebooting')
                                                            : t('header.powerReboot')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                    ]}
                                />
                            )}
                            {canManage && (
                                <Button
                                    variant="secondary"
                                    leftIcon={<IconTerminal size={16} />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openInstanceConsole(instanceData, instanceConsoleMutation);
                                    }}
                                    disabled={
                                        powerState !== 'running' && powerState !== 'rebooting'
                                    }
                                >
                                    {t('header.console')}
                                </Button>
                            )}
                            <Button
                                leftIcon={<IconPlugConnected size={16} />}
                                onClick={openConnectHint}
                                disabled={powerState !== 'running'}
                                className="whitespace-nowrap"
                            >
                                {t('header.connect')}
                            </Button>
                            <Menu withArrow position="bottom">
                                <Menu.Target>
                                    <Button
                                        {...moveTriggerProps('instance', instanceData.id)}
                                        variant="secondary"
                                        aria-label={t('header.moreActions')}
                                        title={t('header.moreActions')}
                                    >
                                        <IconDots size={22} aria-hidden="true" />
                                    </Button>
                                </Menu.Target>

                                <Menu.Dropdown>
                                    <Menu.Item
                                        leftSection={<IconReload size={20} stroke={1.5} />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            refetchInstance();
                                        }}
                                    >
                                        {t('header.refresh')}
                                    </Menu.Item>
                                    {!canManage ? null : instanceData.state.backup === 'enabled' ? (
                                        <Menu.Item
                                            leftSection={<IconPlayerPause size={20} stroke={1.5} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleBackupMutation.mutate(instanceData.id);
                                            }}
                                        >
                                            {t('header.disableBackups')}
                                        </Menu.Item>
                                    ) : (
                                        <Menu.Item
                                            leftSection={<IconPlayerPlay size={20} stroke={1.5} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleBackupMutation.mutate(instanceData.id);
                                            }}
                                        >
                                            {t('header.enableBackups')}
                                        </Menu.Item>
                                    )}
                                    {canManage &&
                                        canMove &&
                                        instanceData.projectId === activeProjectId && (
                                            <Menu.Item
                                                leftSection={
                                                    <IconFolderShare size={20} stroke={1.5} />
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    requestMove({
                                                        id: String(instanceData.id),
                                                        name: instanceData.hostname,
                                                    });
                                                }}
                                            >
                                                {t('projects:resource.moveToProject')}
                                            </Menu.Item>
                                        )}
                                </Menu.Dropdown>
                            </Menu>
                        </div>
                    </div>
                    <PrivateNetworkAlert
                        instanceId={instanceData.id}
                        state={instanceData.privateNetworkState}
                        canManage={canManage}
                    />
                    <Tabs
                        className="w-full mt-8 space-y-4"
                        value={visibleTab}
                        onChange={setActiveTab}
                        color="var(--fg)"
                        keepMounted={false}
                        keepMountedMode="display-none"
                    >
                        <Tabs.List
                            aria-label={t('tabs.label')}
                            className="flex-nowrap overflow-x-auto"
                        >
                            <Tabs.Tab
                                value={'overview'}
                                leftSection={<IconLayoutDashboard size={22} />}
                            >
                                {t('tabs.overview')}
                            </Tabs.Tab>
                            <Tabs.Tab
                                value={'security-group'}
                                leftSection={<IconAffiliate size={22} />}
                            >
                                {t('tabs.networking')}
                            </Tabs.Tab>
                            <Tabs.Tab value={'backup'} leftSection={<IconShieldCheck size={22} />}>
                                {t('tabs.backups')}
                            </Tabs.Tab>
                            {showBlockStorage && (
                                <Tabs.Tab
                                    value={'block-storage'}
                                    leftSection={<IconDatabase size={22} />}
                                >
                                    {t('tabs.blockStorage')}
                                </Tabs.Tab>
                            )}
                            <Tabs.Tab value={'activity'} leftSection={<IconClock size={22} />}>
                                {t('tabs.activity')}
                            </Tabs.Tab>
                            {canManage && (
                                <Tabs.Tab
                                    value={'settings'}
                                    leftSection={<IconSettings size={22} />}
                                >
                                    {t('tabs.settings')}
                                </Tabs.Tab>
                            )}
                        </Tabs.List>

                        <Tabs.Panel
                            value={'overview'}
                            className="w-full flex flex-col justify-start items-center gap-4"
                            style={{ padding: 'none !important' }}
                        >
                            <DialogBox
                                label={t('performance.title')}
                                description={t('performance.liveAutoRefresh')}
                                classNames="p-4"
                            >
                                <SegmentedControl
                                    aria-label={t('performance.filterLabel')}
                                    size="sm"
                                    radius={'md'}
                                    styles={{
                                        root: {
                                            backgroundColor: 'var(--surface-2)',
                                            border: '0.5px solid var(--line)',
                                        },
                                    }}
                                    value={chartFilter}
                                    onChange={setChartFilter}
                                    withItemsBorders={false}
                                    data={[
                                        {
                                            value: 'both',
                                            label: (
                                                <Center style={{ gap: 6 }}>
                                                    <span style={{ fontWeight: 400 }}>
                                                        {t('performance.both')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'cpu',
                                            label: (
                                                <Center
                                                    style={{ gap: 6 }}
                                                    className="inline-flex items-center"
                                                >
                                                    <IconCircleFilled
                                                        size={8}
                                                        className="text-green-400 light:text-green-800"
                                                    />
                                                    <span style={{ fontWeight: 400 }}>
                                                        {t('performance.cpu')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                        {
                                            value: 'memory',
                                            label: (
                                                <Center
                                                    style={{ gap: 6 }}
                                                    className="inline-flex items-center"
                                                >
                                                    <IconCircleFilled
                                                        size={8}
                                                        className="text-blue-400 light:text-blue-700"
                                                    />
                                                    <span style={{ fontWeight: 400 }}>
                                                        {t('performance.memory')}
                                                    </span>
                                                </Center>
                                            ),
                                        },
                                    ]}
                                />
                                <div className="mt-6 mb-4 w-full flex flex-row justify-start items-start gap-6">
                                    {chartFilter === 'both' && (
                                        <>
                                            <div className="flex flex-col justify-start items-start gap-1">
                                                <div className="flex items-center gap-2">
                                                    <IconCircleFilled
                                                        size={8}
                                                        className="text-green-400 light:text-green-800"
                                                    />
                                                    <span className="text-sm text-fg/68">
                                                        {t('performance.cpuUtilization')}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row justify-start items-end gap-1">
                                                    <span className="font-bold text-3xl">
                                                        {currentCpu ?? '--'}
                                                    </span>
                                                    <span className="text-fg/68">%</span>
                                                </div>
                                                <div className="flex flex-row justify-start items-start gap-1 text-xs text-fg/68">
                                                    {stats && stats.cpu && (
                                                        <div className="text-xs text-fg/68">
                                                            {t('performance.minAvgMax', {
                                                                min: stats.cpu.min.toFixed(2),
                                                                avg: stats.cpu.avg.toFixed(2),
                                                                max: stats.cpu.max.toFixed(2),
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col justify-start items-start gap-1">
                                                <div className="flex items-center gap-2">
                                                    <IconCircleFilled
                                                        size={8}
                                                        className="text-blue-400 light:text-blue-700"
                                                    />
                                                    <span className="text-sm text-fg/68">
                                                        {t('performance.memoryUtilization')}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row justify-start items-end gap-1">
                                                    <span className="font-bold text-3xl">
                                                        {currentMemory ?? '--'}
                                                    </span>
                                                    <span className="text-fg/68">%</span>
                                                </div>
                                                <div className="flex flex-row justify-start items-start gap-1 text-xs text-fg/68">
                                                    {t('performance.memoryUsage', {
                                                        used: memoryUsedGB ?? '--',
                                                        max: memoryMaxGB ?? '--',
                                                        free: memoryFreeGB ?? '--',
                                                    })}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {chartFilter === 'cpu' && (
                                        <div className="w-full h-full flex flex-row justify-between items-start">
                                            <div className="w-full flex flex-col justify-start items-start gap-1">
                                                <div className="flex items-center gap-2">
                                                    <IconCircleFilled
                                                        size={8}
                                                        className="text-green-400 light:text-green-800"
                                                    />
                                                    <span className="text-sm text-fg/68">
                                                        {t('performance.cpuUtilization')}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row justify-start items-end gap-1">
                                                    <span className="font-bold text-3xl">
                                                        {currentCpu ?? '--'}
                                                    </span>
                                                    <span className="text-fg/68">%</span>
                                                </div>
                                                <div className="flex flex-row justify-start items-start gap-1 text-xs text-fg/68">
                                                    {t('performance.vcpuLoadAvg', {
                                                        vcpu: instanceData.spec.cpu,
                                                        loadAvg: currentLoadAvg ?? '--',
                                                    })}
                                                </div>
                                            </div>
                                            <div className="w-full h-full flex flex-row justify-end items-end gap-4">
                                                {stats && stats.cpu && (
                                                    <div className="flex flex-row justify-start items-center gap-10 text-xs">
                                                        <div className="flex flex-col justify-end items-end">
                                                            <span className="uppercase font-bold text-fg/68">
                                                                {t('performance.min')}
                                                            </span>
                                                            <span className="font-bold text-sm text-fg">
                                                                {stats.cpu.min.toFixed(2)}%
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col justify-end items-end">
                                                            <span className="uppercase font-bold text-fg/68">
                                                                {t('performance.avg')}
                                                            </span>
                                                            <span className="font-bold text-sm text-fg">
                                                                {stats.cpu.avg.toFixed(2)}%
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col justify-end items-end">
                                                            <span className="uppercase font-bold text-fg/68">
                                                                {t('performance.max')}
                                                            </span>
                                                            <span className="font-bold text-sm text-fg">
                                                                {stats.cpu.max.toFixed(2)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {chartFilter === 'memory' && (
                                        <div className="w-full h-full flex flex-row justify-between items-start">
                                            <div className="w-full flex flex-col justify-start items-start gap-1">
                                                <div className="flex items-center gap-2">
                                                    <IconCircleFilled
                                                        size={8}
                                                        className="text-blue-400 light:text-blue-700"
                                                    />
                                                    <span className="text-sm text-fg/68">
                                                        {t('performance.memoryUtilization')}
                                                    </span>
                                                </div>
                                                <div className="flex flex-row justify-start items-end gap-1">
                                                    <span className="font-bold text-3xl">
                                                        {currentMemory ?? '--'}
                                                    </span>
                                                    <span className="text-fg/68">%</span>
                                                </div>
                                                <div className="flex flex-row justify-start items-start gap-1 text-xs text-fg/68">
                                                    {t('performance.memoryUsage', {
                                                        used: memoryUsedGB ?? '--',
                                                        max: memoryMaxGB ?? '--',
                                                        free: memoryFreeGB ?? '--',
                                                    })}
                                                </div>
                                            </div>
                                            <div className="w-full h-full flex flex-row justify-end items-end gap-4">
                                                {stats && stats.cpu && (
                                                    <div className="flex flex-row justify-start items-center gap-10 text-xs">
                                                        <div className="flex flex-col justify-end items-end">
                                                            <span className="uppercase font-bold text-fg/68">
                                                                {t('performance.min')}
                                                            </span>
                                                            <span className="font-bold text-sm text-fg">
                                                                {stats.memory.min.toFixed(2)}%
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col justify-end items-end">
                                                            <span className="uppercase font-bold text-fg/68">
                                                                {t('performance.avg')}
                                                            </span>
                                                            <span className="font-bold text-sm text-fg">
                                                                {stats.memory.avg.toFixed(2)}%
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col justify-end items-end">
                                                            <span className="uppercase font-bold text-fg/68">
                                                                {t('performance.max')}
                                                            </span>
                                                            <span className="font-bold text-sm text-fg">
                                                                {stats.memory.max.toFixed(2)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="w-full min-w-0 min-h-0">
                                    {chartSeries && (
                                        <AreaChart
                                            key={chartFilter}
                                            h={300}
                                            data={metrics}
                                            dataKey="time"
                                            series={chartSeries}
                                            curveType="bump"
                                            tickLine="none"
                                            withYAxis={false}
                                            withDots={false}
                                        />
                                    )}
                                </div>
                            </DialogBox>
                            <div className="w-full h-auto grid grid-cols-1 xl:grid-cols-[1fr_480px] gap-4">
                                <div className="w-full h-auto flex flex-col justify-start items-center gap-4">
                                    <DialogBox
                                        label={t('systemInformation.title')}
                                        description={t('systemInformation.description')}
                                        classNames="p-4"
                                    >
                                        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4">
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.hostname')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg min-w-0 flex items-center justify-between gap-3">
                                                <span className="min-w-0 break-all">
                                                    {instanceData.hostname}
                                                </span>
                                                <button
                                                    type="button"
                                                    aria-label={t('systemInformation.copyHostname')}
                                                    title={t('systemInformation.copyHostname')}
                                                    onClick={async () => {
                                                        await copyToClipboard(
                                                            instanceData.hostname
                                                        );
                                                        setCopiedHostname(true);
                                                    }}
                                                    className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-line bg-surface-2 px-2 py-1 text-fg/68 transition-colors hover:bg-surface-3 hover:text-fg"
                                                >
                                                    {copiedHostname ? (
                                                        <IconCheck size={14} />
                                                    ) : (
                                                        <IconCopy size={14} />
                                                    )}
                                                </button>
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.publicIpv4')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg min-w-0 flex items-center justify-between gap-3">
                                                <span className="min-w-0 break-all">
                                                    {instanceData.network.ipAddress}
                                                </span>
                                                <button
                                                    type="button"
                                                    aria-label={t(
                                                        'systemInformation.copyPublicIpv4'
                                                    )}
                                                    title={t('systemInformation.copyPublicIpv4')}
                                                    onClick={async () => {
                                                        await copyToClipboard(
                                                            instanceData.network.ipAddress
                                                        );
                                                        setCopiedIpAddress(true);
                                                    }}
                                                    className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-line bg-surface-2 px-2 py-1 text-fg/68 transition-colors hover:bg-surface-3 hover:text-fg"
                                                >
                                                    {copiedIpAddress ? (
                                                        <IconCheck size={14} />
                                                    ) : (
                                                        <IconCopy size={14} />
                                                    )}
                                                </button>
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.privateIpv4')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg min-w-0 flex items-center justify-between gap-3">
                                                {instanceData.network.privateIpAddress ? (
                                                    <>
                                                        <span className="min-w-0 break-all">
                                                            {instanceData.network.privateIpAddress}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            aria-label={t(
                                                                'systemInformation.copyPrivateIpv4'
                                                            )}
                                                            title={t(
                                                                'systemInformation.copyPrivateIpv4'
                                                            )}
                                                            onClick={async () => {
                                                                await copyToClipboard(
                                                                    instanceData.network
                                                                        .privateIpAddress ?? ''
                                                                );
                                                                setCopiedPrivateIpAddress(true);
                                                            }}
                                                            className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-line bg-surface-2 px-2 py-1 text-fg/68 transition-colors hover:bg-surface-3 hover:text-fg"
                                                        >
                                                            {copiedPrivateIpAddress ? (
                                                                <IconCheck size={14} />
                                                            ) : (
                                                                <IconCopy size={14} />
                                                            )}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className="text-fg/68">—</span>
                                                )}
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.operatingSystem')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg">
                                                {getOSFullName(instanceData.image.slug)}
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.plan')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg">
                                                {t(
                                                    PLAN_VALUE_KEY_BY_CATEGORY[
                                                        instanceData.package?.category ?? 'shared'
                                                    ] ?? 'systemInformation.planValueShared',
                                                    {
                                                        cpu: instanceData.spec.cpu,
                                                        ramGb: instanceData.spec.memory / 1024,
                                                    }
                                                )}
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.regionAz')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg">
                                                {t('systemInformation.regionBangkok1')}
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.created')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg">
                                                {dayjs(instanceData.createdAt).format(
                                                    'DD MMM YYYY, HH:mm:ss'
                                                )}
                                            </div>

                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                                                {t('systemInformation.uptime')}
                                            </div>
                                            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg">
                                                {formatUptime(instanceData.state.uptime)}
                                            </div>
                                        </div>
                                    </DialogBox>
                                    <ActivityCard
                                        instanceId={instanceData.id}
                                        onViewAll={() => setActiveTab('activity')}
                                    />
                                </div>
                                <div className="w-full h-auto flex flex-col justify-start items-center gap-4">
                                    <CostCard instanceId={instanceData.id} />
                                    <DialogBox
                                        label={t('tag.title')}
                                        rightDescription={
                                            canManage && (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    leftIcon={<IconPlus size={16} />}
                                                    onClick={openManageTags}
                                                >
                                                    {t('tag.add')}
                                                </Button>
                                            )
                                        }
                                        classNames="p-4"
                                    >
                                        {instanceData.tags.length === 0 ? (
                                            <div className="p-3 w-full flex justify-center items-center">
                                                <div className="text-sm text-fg/68">
                                                    {t('tag.empty')}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="w-full flex flex-wrap items-center gap-2">
                                                    {instanceData.tags.map((tag, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="px-3 py-1 rounded-md bg-surface-2 border border-line flex items-center justify-between gap-2 text-sm text-fg"
                                                        >
                                                            <span className="min-w-0 truncate">
                                                                {tag.name}
                                                            </span>
                                                            {canManage && (
                                                                <button
                                                                    type="button"
                                                                    aria-label={t('tag.deleteTag', {
                                                                        name: tag.name,
                                                                    })}
                                                                    title={t('tag.deleteTag', {
                                                                        name: tag.name,
                                                                    })}
                                                                    onClick={() =>
                                                                        deleteInstanceTagMutation.mutate(
                                                                            {
                                                                                id: instanceData.id,
                                                                                tagId: tag.id,
                                                                            },
                                                                            {
                                                                                onSuccess: () =>
                                                                                    refetchInstance(),
                                                                            }
                                                                        )
                                                                    }
                                                                    className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-line bg-surface-2 px-1 py-1 text-fg/68 transition-colors hover:bg-surface-3 hover:text-fg"
                                                                >
                                                                    <IconX size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </DialogBox>
                                </div>
                            </div>
                        </Tabs.Panel>
                        <Tabs.Panel value={'activity'} className="w-full">
                            <ActivityTabPanel instanceId={instanceData.id} />
                        </Tabs.Panel>
                        <Tabs.Panel value={'backup'} className="w-full">
                            <BackupTabPanel instanceId={instanceData.id} canManage={canManage} />
                        </Tabs.Panel>
                        {showBlockStorage && (
                            <Tabs.Panel value={'block-storage'} className="w-full">
                                <AttachedVolumesPanel
                                    target={{
                                        type: 'vm',
                                        instanceId: instanceData.id,
                                        name: instanceData.hostname,
                                    }}
                                    projectId={instanceData.projectId}
                                    canManageTarget={canManage}
                                />
                            </Tabs.Panel>
                        )}
                        <Tabs.Panel value={'security-group'} className="w-full">
                            <NetworkingTabPanel instance={instanceData} canManage={canManage} />
                        </Tabs.Panel>
                        {canManage && (
                            <Tabs.Panel value={'settings'} className="w-full">
                                <SettingsTabPanel instance={instanceData} />
                            </Tabs.Panel>
                        )}
                    </Tabs>
                </div>
            </div>

            <ConnectInstanceModal
                target={toConnectTarget(instanceData)}
                opened={connectHintOpened}
                onClose={closeConnectHint}
                onOpenConsole={
                    canManage
                        ? () => openInstanceConsole(instanceData, instanceConsoleMutation)
                        : undefined
                }
            />

            <ManageTagsModal
                opened={manageTagsOpened}
                onClose={closeManageTags}
                instance={instanceData}
            />

            {moveModal}
        </>
    );
}
